import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { Checkbox } from '@mui/material';

import DateTextField from '@brands/Components/Inputs/MuiDateTextField/DateTextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import { Loading } from '../../../../Components/LoadingSpinner/Loading';
import MultiSelectInputFreeSolo from '../../../../Components/Inputs/MuiMultiSelectInputFreeSolo/MultiSelectInputFreeSolo';
import MuiToggle from '../../../../Components/Inputs/MuiToggle/MuiToggle';
import TextField from '../../../../Components/Inputs/MuiTextField/TextField';
import QuestionAndRadioButton from '../../../../Components/QuestionAndAnswers/QuestionAndRadioButtonForMed/QuestionAndRadioButton';

import { addClinicalData } from '../../../../services/clinical_data/addClinicalData';
import { listClinicalData } from '../../../../services/clinical_data/listClinicalData';
import { updateClinicalData } from '../../../../services/clinical_data/updateClinicalData';
import { UserProfile, UserRoleName } from '../../../../services/identity/types/UserProfile';
import { displayErrorDetails } from '../../../../Utils/displayError';

import {
  MedicalHistoryDefault,
  MedicalConditionDefault,
  allergyAndMedicationOptions,
  medicationOptions,
  useFollowingQuestions,
} from '../../../MedicalHistory/MedicalHistory/utils/questions';
import { medicalConditionQuestions } from '../../../MedicalHistory/MedicalCondition/utils/questions';
import { visitOptions } from '../../../MedicalHistory/DentalHistory/utils/questions';
import { MedicalConditionSubmit } from '../../../MedicalHistory/MedicalCondition/utils/types';
import { MedicalHistorySubmit } from '../../../MedicalHistory/MedicalHistory/utils/types';
import { combinedValidationSchema } from '../../../MedicalHistory/MedicalHistory/utils/validationSchema';
import styles from '../../../MedicalHistory/MedicalHistory/styles.module.scss';

type YesNo = 'yes' | 'no';

const yesNoOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

type MeasurementMeta = {
  heightUnit: 'cm' | 'ft_in';
  weightUnit: 'kg' | 'lbs';
  heightCM: string;
  weightKG: string;
};

type CombinedForm = MedicalHistorySubmit &
  MedicalConditionSubmit & {
    last_dental_visit?: string;
    who_did_you_see?: string;
    visit_type?: string;
  };

type MedicalHistoryResponse = {
  id?: number;
  height?: string;
  weight?: string;
  height_cm?: number | null;
  weight_kg?: number | null;
  height_unit?: 'cm' | 'ft_in';
  weight_unit?: 'kg' | 'lbs';
  taking_medication?: boolean | null;
  medications?: string | null;
  allergic_to_medications?: boolean | null;
  medications_allergies?: string | null;
  under_care_of_physician?: boolean | null;
  conditions_treated?: string | null;
  using_substances?: string[];
  interested_in_stopping?: boolean | null;
  pharmacy_city?: string | null;
  pharmacy_name?: string | null;
  pharmacy_phone_number?: string | null;
  pharmacy_state?: string | null;
  pharmacy_street_address?: string | null;
  pharmacy_zip_code?: string | null;
};

interface CollectorMedicalHistoryTabProps {
  user: UserProfile | null;
  userRole?: string;
  formRef?: React.RefObject<HTMLFormElement>;
  setIsDirty?: (dirty: boolean) => void;
}

const CollectorMedicalHistoryTab: React.FC<CollectorMedicalHistoryTabProps> = ({
  user,
  userRole = UserRoleName.Collector,
  formRef,
  setIsDirty,
}) => {
  const [loading, setLoading] = useState(false);
  const [historyObjectId, setHistoryObjectId] = useState<number | null>(null);
  const [conditionsObjectId, setConditionsObjectId] = useState<number | null>(null);
  const [dentalObjectId, setDentalObjectId] = useState<number | null>(null);
  const [selectedPatientMedicalHistory, setSelectedPatientMedicalHistory] = useState<MedicalHistoryResponse | null>(null);
  const [initialValues, setInitialValues] = useState<CombinedForm | null>(null);
  /** True when form differs from last loaded/saved snapshot (enable Save). Validation runs on submit. */
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedLastDentalVisit, setSavedLastDentalVisit] = useState('');
  const [savedWhoDidYouSee, setSavedWhoDidYouSee] = useState('');
  const [heightUnit, setHeightUnit] = useState<'cm' | 'ft_in'>('ft_in');
  const [weightUnit, setWeightUnit] = useState<'kg' | 'lbs'>('lbs');
  const [heightCM, setHeightCM] = useState('');
  const [weightKG, setWeightKG] = useState('');
  const [initialMeasurementMeta, setInitialMeasurementMeta] = useState<MeasurementMeta | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
    getValues,
    reset,
    control,
    clearErrors,
  } = useForm<CombinedForm>({
    mode: 'onChange',
    resolver: yupResolver(combinedValidationSchema),
    defaultValues: {
      ...(MedicalConditionDefault as any),
      ...(MedicalHistoryDefault as any),
      last_dental_visit: '',
      who_did_you_see: '',
      visit_type: '',
    },
  });

  const watchedValues = useWatch({ control });

  const onInvalid = (e: any): void => {
    const error = Object.values(e)[0] as any;
    if (error) toast.error(error.message as string);
  };

  useEffect(() => {
    if (!user?.id) return;

    (async (): Promise<void> => {
      try {
        setLoading(true);

        const res = await listClinicalData(user.id, userRole);
        if (res.medical_history) {
          const medicalHistory = res.medical_history as MedicalHistoryResponse;
          setSelectedPatientMedicalHistory(medicalHistory);
          setHistoryObjectId(medicalHistory.id || null);

          if (medicalHistory.height) {
            const [feetPart, inchPart] = medicalHistory.height.split(',');
            const feet = feetPart?.replace('ft', '').trim();
            const inches = inchPart?.replace('in', '').trim();
            setValue('height', medicalHistory.height, { shouldValidate: true });
            setValue('height_feet', feet as any, { shouldValidate: true });
            setValue('height_inches', inches as any, { shouldValidate: true });
          }

          if (medicalHistory.weight) {
            setValue('weight', medicalHistory.weight.split('lb')[0].trim() as any, { shouldValidate: true });
          }

          setValue('taking_medication', medicalHistory.taking_medication as any, { shouldValidate: true });
          setValue('medications', medicalHistory.medications as any, { shouldValidate: true });
          setValue('allergic_to_medications', medicalHistory.allergic_to_medications as any, { shouldValidate: true });
          setValue('medications_allergies', medicalHistory.medications_allergies as any, { shouldValidate: true });
          setValue('under_care_of_physician', medicalHistory.under_care_of_physician as any, {
            shouldValidate: true,
          });
          setValue('conditions_treated', medicalHistory.conditions_treated as any, { shouldValidate: true });

          if (medicalHistory.using_substances) {
            const substances = medicalHistory.using_substances as string[];
            setValue('tobacco_products', substances.includes('Tobacco Products') as any, { shouldValidate: true });
            setValue('recreational_drugs', substances.includes('Recreational Drugs') as any, { shouldValidate: true });
            setValue(
              'prescription_medication_for_non_medical_reasons',
              substances.includes('Prescription medication for non-medical reasons') as any,
              { shouldValidate: true },
            );
          }

          setValue('interested_in_stopping', medicalHistory.interested_in_stopping as any, { shouldValidate: true });

          const nextHeightUnit = (medicalHistory.height_unit || 'ft_in');
          const nextWeightUnit = (medicalHistory.weight_unit || 'lbs');
          let nextHeightCM = '';
          let nextWeightKG = '';

          if (medicalHistory.height_cm) {
            const cm = Math.min(Number(medicalHistory.height_cm), 250);
            nextHeightCM = String(cm);
          } else if (medicalHistory.height) {
            const [feetPart, inchPart] = medicalHistory.height.split(',');
            const feet = Number(feetPart?.replace('ft', '').trim() || 0);
            const inches = Number(inchPart?.replace('in', '').trim() || 0);
            nextHeightCM = String(Math.round((feet * 12 + inches) * 2.54));
          }

          if (medicalHistory.weight_kg) {
            nextWeightKG = String(Math.min(Number(medicalHistory.weight_kg), 250));
          } else if (medicalHistory.weight) {
            const lbs = Number(medicalHistory.weight.replace('lb', '').trim() || 0);
            nextWeightKG = (lbs * 0.453592).toFixed(2);
          }

          setHeightUnit(nextHeightUnit);
          setWeightUnit(nextWeightUnit);
          setHeightCM(nextHeightCM);
          setWeightKG(nextWeightKG);
          setInitialMeasurementMeta({
            heightUnit: nextHeightUnit,
            weightUnit: nextWeightUnit,
            heightCM: nextHeightCM,
            weightKG: nextWeightKG,
          });
        }

        if (res.medical_conditions) {
          setConditionsObjectId(res.medical_conditions.id || null);

          setValue('arthritis', res.medical_conditions.arthritis || false, { shouldValidate: true });
          setValue('cancer', res.medical_conditions.cancer || false, { shouldValidate: true });
          setValue('dementia_alzheimers', res.medical_conditions.dementia_alzheimers || false, { shouldValidate: true });
          setValue('diabetes', res.medical_conditions.diabetes || false, { shouldValidate: true });
          setValue('heart_disease', res.medical_conditions.heart_disease || false, { shouldValidate: true });
          setValue('dry_mouth', res.medical_conditions.dry_mouth || false, { shouldValidate: true });
          setValue('obesity', res.medical_conditions.obesity || false, { shouldValidate: true });
          setValue(
            'pregnancy_or_breastfeeding',
            res.medical_conditions.pregnancy_or_breastfeeding || false,
            { shouldValidate: true },
          );
          setValue(
            'respiratory_conditions',
            res.medical_conditions.respiratory_conditions || false,
            { shouldValidate: true },
          );
          setValue('stroke', res.medical_conditions.stroke || false, { shouldValidate: true });
          setValue(
            'conditions_not_listed',
            res.medical_conditions.conditions_not_listed === 'true'
              ? ''
              : res.medical_conditions.conditions_not_listed || '',
            { shouldValidate: true },
          );
          setValue(
            'medical_condition_none_of_the_above',
            res.medical_conditions.medical_condition_none_of_the_above || false,
            { shouldValidate: true },
          );
        }

        if (res.dental_history) {
          setDentalObjectId(res.dental_history.id || null);
          setValue('last_dental_visit', res.dental_history.last_dental_visit || '', { shouldValidate: true });
          setValue('who_did_you_see', res.dental_history.who_did_you_see || '', { shouldValidate: true });
          setValue('visit_type', res.dental_history.visit_type || '', { shouldValidate: true });
        }

        // Sync internal RHF state to the mapped values.
        const current = getValues();
        reset(current);
        setInitialValues(current);
        setInitialMeasurementMeta((prev) =>
          prev || {
            heightUnit,
            weightUnit,
            heightCM,
            weightKG,
          },
        );
        setHasUnsavedChanges(false);
        // Refresh formState after programmatic load (isValid is unreliable until trigger runs).
        await trigger();
      } catch (e) {
        displayErrorDetails(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id, getValues, reset, setValue, trigger]);

  useEffect(() => {
    if (!initialValues) return;

    const normalizedCurrent = {
      ...watchedValues,
      height: `${watchedValues.height_feet || 0}ft, ${watchedValues.height_inches || 0}in`,
      height_unit: heightUnit,
      weight_unit: weightUnit,
      height_cm: heightCM,
      weight_kg: weightKG,
    };

    const normalizedInitial = {
      ...initialValues,
      height: `${initialValues.height_feet || 0}ft, ${initialValues.height_inches || 0}in`,
      height_unit: initialMeasurementMeta?.heightUnit || 'ft_in',
      weight_unit: initialMeasurementMeta?.weightUnit || 'lbs',
      height_cm: initialMeasurementMeta?.heightCM || '',
      weight_kg: initialMeasurementMeta?.weightKG || '',
    };

    const changed = !isEqual(normalizedCurrent, normalizedInitial);
    setHasUnsavedChanges(changed);
    setIsDirty?.(changed);
  }, [watchedValues, initialValues, heightUnit, weightUnit, heightCM, weightKG, initialMeasurementMeta, setIsDirty]);

  useEffect(() => {
    const allFalse = useFollowingQuestions?.every((item) => !watch(item.id as keyof CombinedForm));
    if (allFalse) {
      setValue('interested_in_stopping', null, { shouldValidate: true });
    }
  }, [
    watch('tobacco_products'),
    watch('recreational_drugs'),
    watch('prescription_medication_for_non_medical_reasons'),
  ]);

  useEffect(() => {
    const noneOfTheAbove = watch('medical_condition_none_of_the_above');
    if (noneOfTheAbove) {
      setValue('arthritis', false, { shouldValidate: true });
      setValue('cancer', false, { shouldValidate: true });
      setValue('dementia_alzheimers', false, { shouldValidate: true });
      setValue('diabetes', false, { shouldValidate: true });
      setValue('heart_disease', false, { shouldValidate: true });
      setValue('dry_mouth', false, { shouldValidate: true });
      setValue('obesity', false, { shouldValidate: true });
      setValue('pregnancy_or_breastfeeding', false, { shouldValidate: true });
      setValue('respiratory_conditions', false, { shouldValidate: true });
      setValue('stroke', false, { shouldValidate: true });
      setValue('conditions_not_listed', '', { shouldValidate: true });
    }
  }, [watch('medical_condition_none_of_the_above')]);

  const onSubmit = async (data: CombinedForm): Promise<void> => {
    try {
      setLoading(true);

      const currentUserId = user?.id;
      if (!currentUserId) return;

      const height = `${data.height_feet || 0}ft, ${data.height_inches || 0}in`;
      const weight = `${data.weight || 0}lb`;
      const using_substances = [
        data.tobacco_products ? 'Tobacco Products' : null,
        data.recreational_drugs ? 'Recreational Drugs' : null,
        data.prescription_medication_for_non_medical_reasons
          ? 'Prescription medication for non-medical reasons'
          : null,
      ].filter(Boolean) as string[];

      const {
        arthritis,
        cancer,
        dementia_alzheimers,
        diabetes,
        heart_disease,
        dry_mouth,
        obesity,
        pregnancy_or_breastfeeding,
        respiratory_conditions,
        stroke,
        conditions_not_listed,
        medical_condition_none_of_the_above,
        ...rest
      } = data;

      const filteredRest = Object.fromEntries(
        Object.entries(rest).filter(
          ([key]) =>
            !['height_feet', 'height_inches', 'tobacco_products', 'recreational_drugs', 'prescription_medication_for_non_medical_reasons'].includes(
              key,
            ),
        ),
      ) as any;

      const {
        pharmacy_city,
        pharmacy_name,
        pharmacy_phone_number,
        pharmacy_state,
        pharmacy_street_address,
        pharmacy_zip_code,
      } = selectedPatientMedicalHistory || {};

      const medicalHistoryPayload: MedicalHistorySubmit = {
        ...(MedicalHistoryDefault as any),
        ...filteredRest,
        user_id: currentUserId,
        height,
        weight,
        using_substances,
        medications: data.taking_medication ? data.medications : '',
        medications_allergies: data.allergic_to_medications ? data.medications_allergies : '',
        conditions_treated: data.under_care_of_physician ? data.conditions_treated : '',
        ...(historyObjectId ? { id: historyObjectId } : {}),
        ...(heightCM ? { height_cm: parseInt(heightCM, 10) } : {}),
        ...(heightUnit ? { height_unit: heightUnit } : {}),
        ...(pharmacy_city ? { pharmacy_city } : {}),
        ...(pharmacy_name ? { pharmacy_name } : {}),
        ...(pharmacy_phone_number ? { pharmacy_phone_number } : {}),
        ...(pharmacy_state ? { pharmacy_state } : {}),
        ...(pharmacy_street_address ? { pharmacy_street_address } : {}),
        ...(pharmacy_zip_code ? { pharmacy_zip_code } : {}),
        ...(weightKG ? { weight_kg: parseFloat(weightKG) } : {}),
        ...(weightUnit ? { weight_unit: weightUnit } : {}),
      };

      const medicalConditionPayload: MedicalConditionSubmit = {
        user_id: currentUserId,
        arthritis,
        cancer,
        dementia_alzheimers,
        diabetes,
        heart_disease,
        dry_mouth,
        obesity,
        pregnancy_or_breastfeeding,
        respiratory_conditions,
        stroke,
        conditions_not_listed: conditions_not_listed === 'false' ? '' : conditions_not_listed,
        medical_condition_none_of_the_above,
      };

      const dentalHistoryPayload = {
        user_id: currentUserId,
        last_dental_visit: data.last_dental_visit || '',
        who_did_you_see: data.who_did_you_see || '',
        visit_type: data.visit_type || '',
      };

      if (!historyObjectId) {
        await addClinicalData('medical_history', medicalHistoryPayload, userRole);
      } else {
        await updateClinicalData(
          'medical_history',
          historyObjectId.toString(),
          medicalHistoryPayload,
          userRole,
        );
      }

      if (!conditionsObjectId) {
        await addClinicalData('medical_conditions', medicalConditionPayload, userRole);
      } else {
        await updateClinicalData(
          'medical_conditions',
          conditionsObjectId.toString(),
          medicalConditionPayload,
          userRole,
        );
      }

      if (!dentalObjectId) {
        await addClinicalData('dental_history', dentalHistoryPayload, userRole);
      } else {
        await updateClinicalData(
          'dental_history',
          dentalObjectId.toString(),
          dentalHistoryPayload,
          userRole,
        );
      }

      const current = getValues();
      setInitialValues(current);
      setInitialMeasurementMeta({
        heightUnit,
        weightUnit,
        heightCM,
        weightKG,
      });
      reset(current);
      setHasUnsavedChanges(false);
      setIsDirty?.(false);

      toast.success('Medical history updated successfully');
    } catch (e) {
      displayErrorDetails(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading dotted />;
  }

  return (
    <div className={styles.ProfileContainer}>
      <div className={`${styles.medicalContainer}`}>
        <form
          ref={formRef}
          className={styles.formContainer}
          style={{
            width: '100%',
            marginRight: '0px',
            justifyContent: 'flex-start',
          }}
          onSubmit={handleSubmit(onSubmit, onInvalid)}
        >
          <div className={styles.wrapper} style={{ maxWidth: '654px' }}>
            <div className={styles.formQuestion} style={{ paddingBottom: '16px', paddingTop: '0px' }}>
              Do you have, or have you had, any of the following medical conditions?
            </div>

            <div className={styles.formSection} style={{ paddingTop: '0px' }}>
              <div className={styles.formContent}>
                {[...medicalConditionQuestions].map((element) => (
                  <QuestionAndRadioButton<CombinedForm>
                    key={element.id}
                    element={element}
                    value={watch(element.id as keyof CombinedForm)}
                    setValue={setValue}
                    trigger={trigger}
                    isMedicalHistory
                  />
                ))}
              </div>
            </div>

            <div className={styles.formSection}>
              <div className={styles.formQuestion}>
                Are you now, or have you been in the past year, under the care of a physician?
              </div>

              <div className={styles.formContent}>
                <MuiToggle
                  {...register('under_care_of_physician')}
                  label="Are you now, or have you been in the past year, under the care of a physician?"
                  hideLabel
                  data-testid="medical-history-under-care-physician"
                  required={false}
                  options={yesNoOptions}
                  selectedValue={
                    watch('under_care_of_physician') === null
                      ? ''
                      : watch('under_care_of_physician')
                        ? 'yes'
                        : 'no'
                  }
                  onChange={(val) => {
                    const isYes = val === 'yes';
                    setValue('under_care_of_physician', isYes as any, { shouldValidate: true });
                    if (!isYes) setValue('conditions_treated', '', { shouldValidate: true });
                  }}
                />
              </div>
            </div>

            {watch('under_care_of_physician') && (
              <div className={styles.formSection}>
                <div className={styles.formQuestion}>What conditions are being treated by your physician?</div>
                <div className={styles.formContent}>
                  <TextField
                    {...register('conditions_treated')}
                    hideLabel
                    multiline
                    minRows={4}
                    value={watch('conditions_treated') || ''}
                    label="What conditions are being treated by your physician?"
                    errors={errors.conditions_treated || {}}
                    onChange={(val) => setValue('conditions_treated', (val.target as any).value, { shouldValidate: true })}
                    inputClass={`${'fs-exclude'}`}
                    wrapperClassName="fs-unmask"
                  />
                </div>
              </div>
            )}

            <div className={styles.formSection}>
              <div>
                <div className={styles.formQuestion}>Are you taking any medications?</div>
                <div className={styles.formQuestionExplaination}>
                  Within the last month, have you taken (or supposed to be taking) any medications? This includes
                  prescription, over-the-counter, diet supplements, and natural or herbal vitamins.
                </div>
              </div>

              <div className={styles.formContent}>
                <MuiToggle
                  {...register('taking_medication')}
                  label="Are you taking any medications?"
                  hideLabel
                  required={false}
                  options={yesNoOptions}
                  selectedValue={watch('taking_medication') === null ? '' : watch('taking_medication') ? 'yes' : 'no'}
                  onChange={(val) => {
                    const isYes = val === 'yes';
                    setValue('taking_medication', isYes as any, { shouldValidate: true });
                    if (!isYes) setValue('medications', '', { shouldValidate: true });
                  }}
                />
              </div>

              {watch('taking_medication') && (
                <div className={styles.formContent} style={{ gap: '8px' }}>
                  <div style={{ fontSize: '14px' }}>Search medications</div>
                  <MultiSelectInputFreeSolo
                    {...register('medications')}
                    label="Search medications"
                    errors={errors.medications ? errors : {}}
                    name="medications"
                    onChange={(selectedValues: any) => {
                      setValue('medications', selectedValues.join(', '), { shouldValidate: true });
                    }}
                    hideLabel
                    styledTag
                    displayCheckbox={false}
                    value={watch('medications')?.split(',').map((med: string) => med.trim())}
                    inputClass={'fs-exclude'}
                    wrapperClassName="fs-unmask"
                    options={medicationOptions}
                  />
                </div>
              )}
            </div>

            <div className={styles.formSection}>
              <div className={styles.formQuestion}>Are you allergic to any medications, metal, latex, or certain materials?</div>
              <div className={styles.formContent}>
                <MuiToggle
                  {...register('allergic_to_medications')}
                  label="Are you allergic?"
                  hideLabel
                  required={false}
                  options={yesNoOptions}
                  selectedValue={watch('allergic_to_medications') === null ? '' : watch('allergic_to_medications') ? 'yes' : 'no'}
                  onChange={(val) => {
                    const isYes = val === 'yes';
                    setValue('allergic_to_medications', isYes as any, { shouldValidate: true });
                    if (!isYes) setValue('medications_allergies', '', { shouldValidate: true });
                  }}
                />
              </div>

              {watch('allergic_to_medications') && (
                <div className={styles.formContent} style={{ gap: '8px' }}>
                  <div style={{ fontSize: '14px' }}>Search allergies</div>
                  <MultiSelectInputFreeSolo
                    {...register('medications_allergies')}
                    label="Search allergies"
                    errors={errors.medications_allergies ? errors : {}}
                    name="medications_allergies"
                    onChange={(selectedValues: any) => {
                      setValue('medications_allergies', selectedValues.join(', '), { shouldValidate: true });
                    }}
                    hideLabel
                    styledTag
                    displayCheckbox={false}
                    value={watch('medications_allergies')?.split(',').map((med: string) => med.trim())}
                    inputClass={'fs-exclude'}
                    wrapperClassName="fs-unmask"
                    options={allergyAndMedicationOptions}
                  />
                </div>
              )}
            </div>

            <div className={styles.formSection}>
              <div className={styles.formQuestion}>Do you use any of the following?</div>
              <div className={styles.formContent}>
                {useFollowingQuestions?.map((element) => (
                  <QuestionAndRadioButton<CombinedForm>
                    key={element.id}
                    element={element}
                    value={watch(element.id as keyof CombinedForm)}
                    setValue={setValue}
                    trigger={trigger}
                  />
                ))}
              </div>
            </div>

            {useFollowingQuestions?.some((item) => watch(item.id as keyof CombinedForm)) && (
              <div className={styles.formSection}>
                <div className={styles.formQuestion}>
                  Are you interested in quitting{' '}
                  {useFollowingQuestions
                    .map((item) => (watch(item.id as keyof CombinedForm) ? item.title.toLowerCase() : ''))
                    .filter(Boolean)
                    .join(', ')}
                  ?
                </div>
                <div className={styles.formContent}>
                  <MuiToggle
                    {...register('interested_in_stopping')}
                    label="Are you interested in quitting [selection]?"
                    hideLabel
                    required={false}
                    options={yesNoOptions}
                    selectedValue={
                      watch('interested_in_stopping') === true
                        ? 'yes'
                        : watch('interested_in_stopping') === false
                          ? 'no'
                          : ''
                    }
                    onChange={(val) => {
                      setValue('interested_in_stopping', val === 'yes' as any, { shouldValidate: true });
                      trigger('interested_in_stopping');
                    }}
                  />
                </div>
              </div>
            )}

            <div className={styles.formSection}>
              <div>
                <div className={styles.formQuestion}>What is your current height and weight?</div>
                <div className={styles.formQuestionExplaination}>
                  We collect this information to ensure you are getting effective and safe prescriptions.
                </div>
              </div>

              <div className={styles.formContent} style={{ gap: '24px' }}>
                <div className={styles.measurementsForm} style={{ width: '360px' }}>
                  <div className={styles.measurementsFormTitle}>How tall are you?</div>
                  <div className={styles.measurementsFormInput}>
                    {heightUnit === 'cm' ? (
                      <input
                        type="number"
                        min={0}
                        max={250}
                        value={heightCM || ''}
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.length > 1 && value.startsWith('0')) value = String(parseInt(value, 10));
                          const cm = Math.min(Number(value || 0), 250);
                          setHeightCM(String(cm));
                          const totalHeightInInches = cm / 2.54;
                          let feet = Math.floor(totalHeightInInches / 12);
                          let inches = Math.round(totalHeightInInches % 12);
                          if (inches === 12) {
                            inches = 0;
                            feet += 1;
                          }
                          setValue('height_feet', String(Math.min(feet, 8)) as any, { shouldValidate: true });
                          setValue('height_inches', String(Math.min(inches, 11)) as any, { shouldValidate: true });
                        }}
                      />
                    ) : (
                      <>
                        <input
                          {...register('height_feet')}
                          type="number"
                          min={0}
                          max={8}
                          value={watch('height_feet') || ''}
                          data-testid="medical-history-height-feet"
                          onChange={(e) => {
                            let value: any = e.target.value;
                            if (value.length > 1 && String(value).startsWith('0')) value = String(parseInt(value, 10));
                            const ft = Math.min(Number(value || 0), 8);
                            setValue('height_feet', String(ft) as any, { shouldValidate: true });
                            setHeightCM(String(Math.round((ft * 12 + Number(watch('height_inches') || 0)) * 2.54)));
                          }}
                        />
                        <input
                          {...register('height_inches')}
                          type="number"
                          min={0}
                          max={11}
                          value={watch('height_inches') || ''}
                          data-testid="medical-history-height-inches"
                          onChange={(e) => {
                            let value: any = e.target.value;
                            if (value.length > 1 && String(value).startsWith('0')) value = String(parseInt(value, 10));
                            const inches = Math.min(Number(value || 0), 11);
                            setValue('height_inches', String(inches) as any, { shouldValidate: true });
                            setHeightCM(String(Math.round((Number(watch('height_feet') || 0) * 12 + inches) * 2.54)));
                          }}
                        />
                      </>
                    )}
                    <MuiToggle
                      label="Use feet/inches or cm?"
                      hideLabel
                      options={[
                        { value: 'yes', label: 'cm' },
                        { value: 'no', label: 'ft/in' },
                      ]}
                      selectedValue={heightUnit === 'cm' ? 'yes' : 'no'}
                      onChange={(u) => setHeightUnit(u === 'yes' ? 'cm' : 'ft_in')}
                      height="40px"
                      width="150px"
                    />
                  </div>
                </div>

                <div className={styles.measurementsForm} style={{ width: '360px' }}>
                  <div className={styles.measurementsFormTitle}>How much do you weigh?</div>
                  <div className={styles.measurementsFormInput}>
                    {weightUnit === 'kg' ? (
                      <input
                        type="number"
                        min={0}
                        max={500}
                        value={weightKG || ''}
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.length > 1 && value.startsWith('0')) value = String(parseInt(value, 10));
                          setWeightKG(value);
                          const totalWeightInLbs = parseFloat(value || '0') / 0.453592;
                          setValue('weight', (totalWeightInLbs ? totalWeightInLbs.toFixed(2) : '') as any, { shouldValidate: true });
                        }}
                        style={{ width: '200px' }}
                      />
                    ) : (
                      <input
                        {...register('weight')}
                        type="number"
                        min={0}
                        max={1000}
                        value={watch('weight') || ''}
                        data-testid="medical-history-weight"
                        onChange={(e) => {
                          let value: any = e.target.value;
                          if (value.length > 1 && String(value).startsWith('0')) value = String(parseInt(value, 10));
                          setValue('weight', value as any, { shouldValidate: true });
                          const totalWeightInKg = parseFloat(value || '0') * 0.453592;
                          setWeightKG(totalWeightInKg ? totalWeightInKg.toFixed(2) : '');
                        }}
                        style={{ width: '200px' }}
                      />
                    )}
                    <MuiToggle
                      label="Use pounds or kg?"
                      hideLabel
                      options={[
                        { value: 'yes', label: 'kg' },
                        { value: 'no', label: 'lbs' },
                      ]}
                      selectedValue={weightUnit === 'kg' ? 'yes' : 'no'}
                      onChange={(u) => setWeightUnit(u === 'yes' ? 'kg' : 'lbs')}
                      height="40px"
                      width="150px"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.formSection} style={{ borderBottom: 'none' }}>
              <div className={styles.formQuestion}>When is the last time you saw a dentist in person?</div>
              <div className={styles.formContent} style={{ gap: '16px', flexDirection: 'row' }}>
                <DateTextField
                  {...register('last_dental_visit')}
                  label="Last dental appointment"
                  errors={errors as any}
                  name="last_dental_visit"
                  onChange={(newValue: any) => {
                    if (errors.last_dental_visit) clearErrors('last_dental_visit');
                    const formattedDate = newValue ? dayjs(newValue).format('MM/DD/YYYY') : '';
                    setValue('last_dental_visit', formattedDate, { shouldValidate: true });
                  }}
                  value={
                    watch('last_dental_visit') && watch('last_dental_visit') !== 'dontKnow'
                      ? dayjs(watch('last_dental_visit') as string)
                      : null
                  }
                  inputClass="fs-exclude"
                  wrapperClassName="fs-unmask"
                  wrapperStyle={{ width: '100%' }}
                />
                <div className={styles.checkboxContainer} style={{ whiteSpace: 'nowrap' }}>
                  <Checkbox
                    className={styles.checkboxInput}
                    checked={watch('last_dental_visit') === 'dontKnow'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        setSavedLastDentalVisit((watch('last_dental_visit') as string) || '');
                        setValue('last_dental_visit', 'dontKnow', { shouldValidate: true });
                      } else {
                        setValue('last_dental_visit', savedLastDentalVisit || '', { shouldValidate: true });
                      }
                      if (errors.last_dental_visit) clearErrors('last_dental_visit');
                    }}
                    name="last_dental_visit_not_sure"
                  />
                  <label className={styles.checkboxLabel} htmlFor="last_dental_visit_not_sure">
                    Not sure
                  </label>
                </div>
              </div>
              <div className={styles.formContent} style={{ gap: '16px', flexDirection: 'row' }}>
                <TextField
                  {...register('who_did_you_see')}
                  value={watch('who_did_you_see') === 'dontKnow' ? '' : watch('who_did_you_see') || ''}
                  label="Dentist Seen"
                  errors={errors.who_did_you_see || {}}
                  onChange={(val) => setValue('who_did_you_see', val.target.value, { shouldValidate: true })}
                  inputClass="fs-exclude"
                  wrapperClassName="fs-unmask"
                  wrapperStyle={{ width: '100%' }}
                />
                <div className={styles.checkboxContainer} style={{ whiteSpace: 'nowrap' }}>
                  <Checkbox
                    className={styles.checkboxInput}
                    checked={watch('who_did_you_see') === 'dontKnow'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        setSavedWhoDidYouSee((watch('who_did_you_see') as string) || '');
                        setValue('who_did_you_see', 'dontKnow', { shouldValidate: true });
                      } else {
                        setValue('who_did_you_see', savedWhoDidYouSee || '', { shouldValidate: true });
                      }
                      if (errors.who_did_you_see) clearErrors('who_did_you_see');
                    }}
                    name="who_did_you_see_not_sure"
                  />
                  <label className={styles.checkboxLabel} htmlFor="who_did_you_see_not_sure">
                    Not sure
                  </label>
                </div>
              </div>
              <div className={styles.formContent}>
                <MUISelectInput
                  {...register('visit_type')}
                  label="Reason for visit (optional)"
                  errors={errors.visit_type ? (errors as any) : {}}
                  name="visit_type"
                  onChange={(selectedValue: string) => {
                    if (errors.visit_type) clearErrors('visit_type');
                    setValue('visit_type', selectedValue, { shouldValidate: true });
                  }}
                  value={watch('visit_type') || ''}
                  inputClass="fs-exclude"
                  wrapperClassName="fs-unmask"
                  options={visitOptions}
                />
              </div>
            </div>

            <div className={styles.btnContainer}>
              <button
                type="submit"
                data-testid="medical-history-save"
                className={styles.saveButton}
                disabled={!hasUnsavedChanges || loading}
              >
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CollectorMedicalHistoryTab;
